import * as React from "react";
import * as Yup from "yup";
import { Form, Formik, FormikHelpers, FormikValues } from "formik";
import { Box } from "@mui/material";
import toast from "react-hot-toast";
import config from "../../../config";
import axiosInstant from "../../../api";
import { FieldText } from "../../../components/fields/Text/Text";
import { IconCloseEye, IconEye } from "../../../utils/icons";

const initialValues = {
  oldPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const ValidationSchema = Yup.object().shape({
  oldPassword: Yup.string().required("Old password is required"),
  newPassword: Yup.string()
    .min(8, "Your password is too short")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Confirm password is required"),
});

export default function ResetPassword() {
  const [showOldPassword, setShowOldPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const handleChangePassword = async (
    values: FormikValues,
    { setSubmitting, resetForm, setStatus }: FormikHelpers<typeof initialValues>
  ) => {
    setStatus(undefined);
    setSubmitting(true);

    try {
      const payload = {
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      };
      await axiosInstant.patch(`${config.API_URL}/users/me/password`, payload);
      toast.success("Password updated successfully!");
      resetForm();
    } catch (error: any) {
      let msg = "Ooops! Something went wrong";
      const data = error?.response?.data;
      const code =
        typeof data === "string" ? data : data?.error || data?.message;
      switch (code) {
        case "PasswordMismatch":
          msg = "Current password is incorrect";
          break;
        case "NoPasswordSet":
          msg = "This account has no password yet. Use Forgot password to set one.";
          break;
        case "UserNotFound":
          msg = "User not found";
          break;
        case "Unauthorized":
          msg = "Your session has expired. Please sign in again.";
          break;
        default:
          if (error?.response?.status === 400) {
            msg = "Please check your inputs and try again";
          } else if (!error?.response) {
            msg = "Network error. Please check your connection and try again";
          }
      }
      setStatus({ error: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const getPasswordVisibilityIcon = (
    isVisible: boolean,
    onToggle: (e: boolean) => void
  ) => (
    <Box
      position="absolute"
      sx={{ right: 10, top: 48, cursor: "pointer" }}
      onClick={() => onToggle(!isVisible)}
    >
      {isVisible ? <IconCloseEye /> : <IconEye stroke="#868686" />}
    </Box>
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={ValidationSchema}
      onSubmit={handleChangePassword}
    >
      {({ isSubmitting, status }) => (
        <Form className="w-full">
          <h3 className="text-text-1 text-xl sm:text-3xl mb-3 font-bold uppercase font-[CircularStd]">
            Reset Password
          </h3>

          <Box display="flex" flexDirection="column" gap={3}>
            <div className="relative">
              <FieldText
                label="Current Password"
                placeholder="Current Password"
                name="oldPassword"
                type={showOldPassword ? "text" : "password"}
                fullWidth
              />
              {getPasswordVisibilityIcon(showOldPassword, setShowOldPassword)}
            </div>
            <div className="relative">
              <FieldText
                label="New Password"
                placeholder="New Password"
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                fullWidth
              />
              {getPasswordVisibilityIcon(showNewPassword, setShowNewPassword)}
            </div>

            <div className="relative">
              <FieldText
                label="Confirm Password"
                placeholder="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                fullWidth
              />
              {getPasswordVisibilityIcon(
                showConfirmPassword,
                setShowConfirmPassword
              )}
            </div>
          </Box>
          <button
            className="bg-accent text-sm sm:text-base text-white mt-8 sm:mt-12 py-2.5 sm:py-3 px-4 sm:px-5 lg:px-10 w-full sm:w-auto rounded-xl focus:outline-none focus:shadow-outline disabled:opacity-60 disabled:cursor-not-allowed"
            type="submit"
            disabled={isSubmitting}
          >
            Save changes
          </button>
          {status?.error ? (
            <div className="text-left text-error text-xs font-bold mt-1">
              {status.error}
            </div>
          ) : (
            ""
          )}
        </Form>
      )}
    </Formik>
  );
}
